import { useEffect, useMemo, useState } from 'react';
import ChestSpinnerPlatform from '../../../../assets/images/chests/spinner/chest-spinner-platform.png';
import Button from '../../../../Components/Button';
import { formatNumbers } from '../../../../helpers';
import StarIcon from '../../../../icons/Star';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { buyChest, openChest } from '../../../../store/reducers/chests/asyncActions';
import ChestSpinner from '../../../../templates/ChestSpinner';
import { useSpinAnimation } from '../../../../templates/ChestSpinner/hooks/useSpineAnimation';
import { TChestRewardHistory } from '../../../../types';
import { PaymentEvents } from '../../../../constants/events';
import { TChestOwnedPaymentEventData } from '../../../../types/events';

type TChesSpinContent = {
    onFinish: (reward: TChestRewardHistory) => void;
}

function ChesSpinContent({ onFinish }: TChesSpinContent) {
  const dispatch = useAppDispatch();

  const { activeChest } = useAppSelector(({ chests }) => chests);
  const { list: paymentsList } = useAppSelector(({ payments }) => payments);

  const [loading, setLoading] = useState({ buying: false });

  const {
    scope, spin, x, spinLine, isSpinning,
  } = useSpinAnimation({ rewards: activeChest?.chest_rewards ?? [] }, {
    onFinish,
  });

  const activePayment = useMemo(() => paymentsList.find((p) => p.item_type === 'chest' && p.item_id === activeChest?.id), [paymentsList]);

  useEffect(() => {
    const onEvent = async (evt: CustomEvent<TChestOwnedPaymentEventData>) => {
      const result = await dispatch(openChest({ id: evt.detail.chestHistory.id }));

      if (result.error) {
        return;
      }

      if (activeChest?.id === result.payload.chest_id) {
        spin(result.payload);
      }
    };

    window.addEventListener(PaymentEvents.CHEST_OWNED, onEvent);

    return () => {
      window.removeEventListener(PaymentEvents.CHEST_OWNED, onEvent);
    };
  }, [scope]);

  const onInvoiceOpen = (invoiceLink: string) => {
    try {
      window.Telegram.WebApp.openInvoice(invoiceLink, (status) => {
        window.console.log(status);
      });
    } catch {
      window.open(invoiceLink, '_blank');
    }
  };

  const onBuy = async () => {
    if (!activeChest) {
      return;
    }

    setLoading((prev) => ({ ...prev, buying: true }));

    const result = await dispatch(buyChest({ id: activeChest.id }));

    setLoading((prev) => ({ ...prev, buying: false }));

    if (result.error) {
      return;
    }

    onInvoiceOpen(result.payload.invoice_link);
  };

  const onClick = () => {
    if (activePayment?.status === 'pending') {
      onInvoiceOpen(activePayment.invoice_link);
    } else {
      onBuy();
    }
  };

  if (!activeChest) {
    return null;
  }

  return (
    <>
      <div className="absolute bottom-[56px] w-full">
        <img src={ChestSpinnerPlatform} alt="chest-spinner-platform" className="w-full" />
      </div>
      <div className="mt-[39px]">
        <ChestSpinner scope={scope} spinLine={spinLine} x={x} chest={activeChest} multiplier={null} />
      </div>
      <Button
        className={`
          btn navy 
          flex items-center
          text-[24px]
          w-[calc(100%-26px)]
          absolute
          bottom-[83px]
          inset-x-0
          mx-auto
        `}
        pureClass
        onClick={onClick}
        disabled={loading.buying || isSpinning}
      >
        Open for
        {' '}
        <StarIcon size={38} className="mr-[-8px] mt-[-4px]" />
        {formatNumbers(activeChest.price, 0)}
      </Button>
    </>
  );
}

export default ChesSpinContent;
